import React, { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import CustomTable from '../../components/CustomTable/CustomTable';
import CustomModal from '../../components/CustomModal';
import Title from '../../components/Title';
import Pagination from '../../components/Pagination';
import { useDispatch, useSelector } from 'react-redux';
import { Space } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
    allContacts,
    deleteContact,
    contactsLoadingStatus,
    contactsMeta,
    getContacts,
} from '../../redux/slices/contactSlice';
import { toast } from 'react-toastify';

export default function ContactPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const contacts = useSelector(allContacts);
    const contactsStatus = useSelector(contactsLoadingStatus);
    const contactsMetainformation = useSelector(contactsMeta);

    const [isDeletePageModalVisible, setIsDeletePageModalVisible] =
        useState(false);
    const [pageData, setPageData] = useState({});
    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(getContacts({ name: '' }));
    }, [dispatch]);

    const onCreatePageClick = () => {
        navigate('create');
    };

    const onEditPage = (record, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        navigate(`${record.id}/edit`);
    };

    const toggleDeletePageModal = (record, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (!record) {
            setIsDeletePageModalVisible(!isDeletePageModalVisible);
            setErrors({});
        } else {
            setPageData({ name: record.title, id: record.id });
            setIsDeletePageModalVisible(!isDeletePageModalVisible);
            setErrors({});
        }
    };

    const onDeleteContact = async () => {
        try {
            await dispatch(deleteContact(pageData.id)).unwrap();
            toast.success('Package deleted successfully');
            toggleDeletePageModal();
            dispatch(getContacts({ name: '' }));
        } catch (error) {
            toast.error(error.errors);
            setErrors(error.errors);
        }
    };

    const getContactsPage = (page) => {
        dispatch(
            getContacts({
                page,
            }),
        );
    };

    const paginationControlls = {
        previousClick: getContactsPage,
        nextClick: getContactsPage,
        pageChange: getContactsPage,
    };

    const actionsOption = {
        title: 'Actions',
        render: (_, record) => (
            <Space size="middle">
                <EditOutlined onClick={(e) => onEditPage(record, e)} />
                <DeleteOutlined
                    onClick={(e) => toggleDeletePageModal(record, e)}
                />
            </Space>
        ),
    };

    const formattedContacts = useMemo(
        () =>
            contacts?.map((el) => {
                return {
                    ...el,
                    market: el.market.join(', '),
                };
            }),
        [contacts],
    );

    return (
        <div>
            <Helmet>
                <title>Console Defentry - Contact pages</title>
            </Helmet>
            <Title>Contact pages</Title>
            <div className="table-wrapper">
                <CustomTable
                    tableHeader={[
                        'ID',
                        'Page title',
                        'Partner',
                        'Market',
                        'Provider',
                        'Actions',
                    ]}
                    columnsDataMap={{
                        Provider: 'vendor',
                        'Page title': 'title',
                    }}
                    functionalCell={{ Actions: actionsOption }}
                    onAddButtonClick={onCreatePageClick}
                    tablePannelTitle="Contact pages"
                    searchInputPlaceholder="Search by name"
                    topPannelButtonText="Add new page"
                    data={formattedContacts}
                    isLoading={contactsStatus === 'loading'}
                />
                {contactsStatus === 'succeeded' && (
                    <Pagination
                        data={contactsMetainformation}
                        controlls={paginationControlls}
                    />
                )}
            </div>
            <CustomModal
                title={
                    <p>
                        Are you sure you want to delete
                        <span className="highlighted-text">{` ${pageData.name} `}</span>
                        page?
                    </p>
                }
                subText="You will delete this page without recovery"
                subTextAlign="center"
                isModalVisible={isDeletePageModalVisible}
                onAcceptButtonClick={onDeleteContact}
                onCancelButtonClick={toggleDeletePageModal}
                cancelButtonText="Cancel"
                acceptButtonText="Delete"
            >
                {errors.package && (
                    <p className="modal__error-text">{errors.package}</p>
                )}
            </CustomModal>
        </div>
    );
}
